import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Rocket, Check, Loader2, Vote, Lightbulb, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { successPop } from "@/lib/celebrate";

export function IdeaVote() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);
  const [idea, setIdea] = useState("");
  useEffect(() => { api.get(user ? "/ideas" : "/public/ideas").then(({ data }) => setD(data)).catch(() => setD({ ideas: [] })); }, [user]);
  if (!d) return <div className="h-60 rounded-[2rem] bg-white animate-pulse" />;
  const vote = async (id, e) => {
    const el = e.currentTarget;
    setBusy(id);
    const had = d.my_votes.includes(id);
    try { const { data } = await api.post(`/ideas/${id}/vote`); setD(data); if (!had) { successPop(el); toast.success("Danke für deine Stimme 💖"); } else toast("Stimme zurückgenommen"); }
    catch (er) { toast.error(er?.response?.data?.detail || "Das hat nicht geklappt"); } finally { setBusy(null); }
  };
  const suggest = async (e) => {
    e.preventDefault();
    setBusy("new");
    try { const { data } = await api.post("/ideas", { title: idea.trim() }); setD(data); setIdea(""); toast.success("Danke! Wir schauen uns deine Idee an und schalten sie frei 💡"); }
    catch (er) { const m = er?.response?.data?.detail; toast.error(typeof m === "string" ? m : "Bitte 3–80 Zeichen eingeben"); } finally { setBusy(null); }
  };
  const total = d.ideas.reduce((s, i) => s + i.votes, 0);
  return (
    <section className="rounded-[2rem] bg-white border border-pink-100 p-6 sm:p-8" data-testid="support-ideas">
      <h2 className="font-display text-2xl font-extrabold flex items-center gap-2"><Vote className="w-6 h-6 text-rose-500" /> Was soll als Nächstes kommen?</h2>
      <p className="text-sm text-slate-500 mt-1">Stimm ab, welche Idee wir als Nächstes angehen – du kannst jede Idee einmal unterstützen und deine Stimme jederzeit zurücknehmen. {total > 0 && <b className="text-slate-700">{total} {total === 1 ? "Stimme" : "Stimmen"} bisher.</b>}</p>
      <ul className="mt-4 space-y-2">
        {d.ideas.map((i) => {
          const mine = d.my_votes.includes(i.id);
          return (
            <li key={i.id}>
              <button type="button" disabled={!user || busy} onClick={(e) => vote(i.id, e)} data-testid={`idea-vote-${i.id}`}
                className={`relative w-full overflow-hidden rounded-2xl border px-4 py-3 text-left transition-colors ${mine ? "border-rose-400 bg-rose-50" : "border-pink-100 bg-white hover:border-rose-300"} disabled:cursor-default`}>
                <span className="absolute inset-y-0 left-0 bg-rose-100/70 transition-[width] duration-700" style={{ width: `${i.percent}%` }} />
                <span className="relative flex items-center gap-3">
                  {busy === i.id ? <Loader2 className="w-4 h-4 animate-spin text-rose-500" /> : mine ? <Check className="w-4 h-4 text-rose-600" /> : <Rocket className="w-4 h-4 text-rose-400" />}
                  <span className="flex-1 text-sm font-semibold text-slate-800">{i.title}</span>
                  <span className="text-xs font-bold text-slate-500 tabular-nums" data-testid={`idea-percent-${i.id}`}>{i.percent} %</span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {!user && <p className="mt-3 text-sm text-slate-500"><Link to="/registrieren" className="font-bold text-rose-600" data-testid="idea-login-link">Melde dich kostenlos an</Link>, um mitzustimmen.</p>}
      {user && !d.my_votes.length && <p className="mt-3 text-xs text-slate-400">Tippe auf eine Idee, um abzustimmen.</p>}
      {user && (
        <form onSubmit={suggest} className="mt-5 pt-5 border-t border-pink-100" data-testid="idea-suggest-form">
          <p className="text-sm font-bold text-slate-700 flex items-center gap-2"><Lightbulb className="w-4 h-4 text-amber-500" /> Eigene Idee vorschlagen</p>
          <div className="mt-2 flex gap-2">
            <input value={idea} onChange={(e) => setIdea(e.target.value)} maxLength={80} placeholder="z. B. Monatliches Live-Q&A" data-testid="idea-suggest-input"
              className="flex-1 min-w-0 h-11 rounded-full border border-pink-200 px-4 text-sm outline-none focus:border-rose-400" />
            <button type="submit" disabled={idea.trim().length < 3 || busy} data-testid="idea-suggest-submit"
              className="h-11 px-5 rounded-full pink-gradient text-white text-sm font-bold disabled:opacity-50 inline-flex items-center gap-2">
              {busy === "new" && <Loader2 className="w-4 h-4 animate-spin" />}Senden
            </button>
          </div>
          <p className="mt-1.5 text-xs text-slate-400">Wir prüfen jede Idee kurz, bevor sie zur Abstimmung erscheint.</p>
          {d.my_pending?.length > 0 && (
            <ul className="mt-3 space-y-1" data-testid="idea-my-pending">
              {d.my_pending.map((p) => <li key={p.id} className="text-xs text-slate-500 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-amber-500" /> „{p.title}" – wartet auf Freigabe</li>)}
            </ul>
          )}
        </form>
      )}
    </section>
  );
}
